import { useTranslation } from 'react-i18next';
import { LayoutList, Inbox, Wrench, CheckCircle2, PackageCheck, BadgeCheck, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type HistoryStatus = 'all' | 'accepted' | 'diagnosed' | 'fixed' | 'picked' | 'completed' | 'cancelled';

export const HISTORY_STATUSES: { value: HistoryStatus; labelKey: string; icon: LucideIcon; iconClass: string }[] = [
  { value: 'all', labelKey: 'filterAll', icon: LayoutList, iconClass: 'bg-blue-600' },
  { value: 'accepted', labelKey: 'accepted', icon: Inbox, iconClass: 'bg-yellow-400' },
  { value: 'diagnosed', labelKey: 'filterDiagnosed', icon: Wrench, iconClass: 'bg-indigo-500' },
  { value: 'fixed', labelKey: 'fixed', icon: CheckCircle2, iconClass: 'bg-green-500' },
  { value: 'picked', labelKey: 'picked', icon: PackageCheck, iconClass: 'bg-emerald-600' },
  { value: 'completed', labelKey: 'filterCompleted', icon: BadgeCheck, iconClass: 'bg-gray-400' },
  { value: 'cancelled', labelKey: 'cancelled', icon: XCircle, iconClass: 'bg-red-500' },
];

type HistoryStatusBarProps = {
  value: HistoryStatus;
  onChange: (value: HistoryStatus) => void;
  counts: Partial<Record<HistoryStatus, number>>;
};

/** Horizontal row of status cards with counts; clicking a card filters the history list. */
const HistoryStatusBar = ({ value, onChange, counts }: HistoryStatusBarProps) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg shadow-sm p-2 md:p-3 mb-3 md:mb-4">
      <div
        className="flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {HISTORY_STATUSES.map(({ value: status, labelKey, icon: Icon, iconClass }) => {
          const active = value === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => onChange(status)}
              aria-pressed={active}
              // Phones: compact "label count" chips that scroll sideways.
              // md+: cards with count, label and coloured icon.
              className={`flex-shrink-0 md:flex-shrink md:flex-1 md:min-w-[140px] flex items-center justify-between gap-2 md:gap-3 px-3 py-1.5 md:py-3 rounded-full md:rounded-lg border text-left transition-colors ${
                active
                  ? 'bg-blue-50 border-blue-500'
                  : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="min-w-0 flex md:block items-baseline gap-1.5 flex-row-reverse">
                <div className="text-sm md:text-xl font-bold text-blue-700 leading-tight">
                  {counts[status] ?? '–'}
                </div>
                <div className="text-sm text-gray-700 whitespace-nowrap md:truncate">{t(labelKey)}</div>
              </div>
              <span className={`hidden md:flex flex-shrink-0 w-9 h-9 rounded-lg items-center justify-center text-white ${iconClass}`}>
                <Icon className="w-5 h-5" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HistoryStatusBar;
